import { ReactNode } from 'react';
import {
  ActionAccordionAdditionalColumn,
  ActionAccordionRowData,
  ActionAccordionRowType,
} from '@/types/model/actionAccordion';

export interface ActionAccordionAdapterContext<TSource> {
  source: TSource;
  index: number;
  pathIds: number[];
  depth: number;
  parent?: TSource;
}

export interface ActionAccordionAdapterRow<TSource> {
  id: number;
  type: ActionAccordionRowType;
  number: string;
  title: string;
  isActive?: boolean;
  isExpanded?: boolean;
  isExpandable?: boolean;
  contentCount?: number;
  isSelectable?: boolean;
  isSelected?: boolean;
  content?: ReactNode;
  additionalColumns?: ActionAccordionAdditionalColumn[];
  children?: TSource[];
}

export interface ActionAccordionAdapterConfig<TSource> {
  getChildren?: (source: TSource) => TSource[] | undefined;
  mapRow: (context: ActionAccordionAdapterContext<TSource>) => ActionAccordionAdapterRow<TSource>;
}

export interface ActionAccordionAdapter<TSource> {
  toRows: (sources: TSource[], parent?: TSource, parentPathIds?: number[], depth?: number) => ActionAccordionRowData[];
}

const buildPathId = (parentPathIds: number[], id: number): number[] => [...parentPathIds, id];

export const createActionAccordionAdapter = <TSource>(
  config: ActionAccordionAdapterConfig<TSource>
): ActionAccordionAdapter<TSource> => {
  const toRows = (
    sources: TSource[],
    parent?: TSource,
    parentPathIds: number[] = [],
    depth = 0
  ): ActionAccordionRowData[] => {
    return sources.map((source, index) => {
      const row = config.mapRow({
        source,
        index,
        pathIds: parentPathIds,
        depth,
        parent,
      });

      const nextPathIds = buildPathId(parentPathIds, row.id);
      const sourceChildren = config.getChildren?.(source) ?? [];
      const mappedChildren = sourceChildren.length > 0
        ? toRows(sourceChildren, source, nextPathIds, depth + 1)
        : undefined;

      return {
        id: row.id,
        type: row.type,
        number: row.number,
        title: row.title,
        isActive: row.isActive,
        isExpanded: row.isExpanded ?? false,
        isExpandable: row.isExpandable,
        contentCount: row.contentCount,
        isSelectable: row.isSelectable,
        isSelected: row.isSelected,
        content: row.content,
        additionalColumns: row.additionalColumns,
        children: mappedChildren,
      };
    });
  };

  return { toRows };
};
